import { createContext, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sectorService } from '../../../shared/services/sector/sectorService';
import { useTenant } from '../../../shared/context/TenantContext';


export interface Sector {
  id: string;
  name: string;
  description: string;
  tenant: {
    id: string;
    name: string;
    fantasyName: string;
  };
  tenant_id?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface SectorContextData {
  sectors: Sector[];
  isLoading: boolean;
  error: string | null;
  handleCreate: () => void;
  handleView: (sector: Sector) => void;
  handleCloseError: () => void;
  isDeleteModalOpen: boolean;
  selectedSector: Sector | null;
  openDeleteModal: (sector: Sector) => void;
  closeDeleteModal: () => void;
  handleDeleteSector: () => void;
  isDeleting: boolean;
  navigateToEdit: (id: string) => void;
  sectorData: Sector | null;
  isDataLoading: boolean;
  createSector: (data: Omit<Sector, 'id' | 'tenant'>) => Promise<void>;
  updateSector: (id: string, data: Partial<Sector>) => Promise<void>;
  isSubmitting: boolean;
  setError: (error: string | null) => void;
}

interface SectorProviderProps {
  children: ReactNode;
}

export const SectorContext = createContext<SectorContextData>({} as SectorContextData);

export function SectorProvider({ children }: SectorProviderProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const { selectedTenant } = useTenant();

  const { data: sectors = [], isLoading } = useQuery({
    queryKey: ['sectors', selectedTenant],
    queryFn: async () => {
      try {
        const response = await sectorService.list({ tenantId: selectedTenant.trim() });
        return response.data;
      } catch (error: any) {
        setError(error?.response?.data?.message || error.message || 'Erro ao buscar setores');
        return [];
      }
    },
    enabled: !!selectedTenant,
  });

  const { data: sectorData = null, isLoading: isDataLoading } = useQuery({
    queryKey: ['sector', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const response = await sectorService.get(id);
        return response.data;
      } catch (error: any) {
        setError(error?.response?.data?.message || error.message || 'Erro ao buscar setor');
        return null;
      }
    },
    enabled: !!id,
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSector, setSelectedSector] = useState<Sector | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => sectorService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sectors'] });
      navigate('/sectors');
    }
  });

  const handleCreate = () => navigate('/sectors/new');
  const handleView = (sector: Sector) => navigate(`/sectors/${sector.id}`);
  const handleCloseError = () => setError(null);

  const openDeleteModal = (sector: Sector) => {
    setSelectedSector(sector);
    setIsDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedSector(null);
  };
  const handleDeleteSector = () => {
    if (selectedSector?.id) {
      deleteMutation.mutate(selectedSector.id);
    }
  };
  const navigateToEdit = (id: string) => navigate(`/sectors/${id}/edit`);

  const createMutation = useMutation({
    mutationFn: (data: Omit<Sector, 'id' | 'tenant'>) => sectorService.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sectors'] }),
    onError: (error: any) => setError(error?.response?.data?.message || error.message || 'Erro ao criar setor')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Sector> }) => sectorService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sectors'] });
      queryClient.invalidateQueries({ queryKey: ['sector', id] });
    },
    onError: (error: any) => setError(error?.response?.data?.message || error.message || 'Erro ao atualizar setor')
  });

  const createSector = async (data: Omit<Sector, 'id' | 'tenant'>) => {
    await createMutation.mutateAsync(data);
  };
  const updateSector = async (id: string, data: Partial<Sector>) => {
    await updateMutation.mutateAsync({ id, data });
  };

  return (
    <SectorContext.Provider
      value={{
        sectors,
        isLoading,
        error,
        handleCreate,
        handleView,
        handleCloseError,
        isDeleteModalOpen,
        selectedSector,
        openDeleteModal,
        closeDeleteModal,
        handleDeleteSector,
        isDeleting: deleteMutation.isPending,
        navigateToEdit,
        sectorData,
        isDataLoading,
        createSector,
        updateSector,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
        setError
      }}
    >
      {children}
    </SectorContext.Provider>
  );
}